/**
 * Beneficiaries API Client
 *
 * Provides methods for fetching and managing policy beneficiaries.
 * All fetch methods unwrap the standard API envelope
 * { success, message, timestamp, data: T } and return T directly.
 */

export type BeneficiaryRelationship =
  | "spouse"
  | "child"
  | "parent"
  | "sibling"
  | "grandparent"
  | "grandchild"
  | "business_partner"
  | "other";

export interface Beneficiary {
  id: string;
  name: string;
  relationship: BeneficiaryRelationship;
  percentage: number;
  date_of_birth?: string | null;
  contact_number?: string | null;
  is_primary: boolean;
  contingent_order?: number | null;
}

// ─── Response types (unwrapped — match the `data` field of the API envelope) ─

export interface BeneficiariesListResponse {
  beneficiaries: Beneficiary[];
  policy_id: string;
  total_primary_allocation: number;
  total_contingent_allocation: number;
}

/** fetchById returns the Beneficiary directly (backend: data: Beneficiary) */
export type BeneficiaryDetailResponse = Beneficiary;

export interface BeneficiaryMutationResponse {
  beneficiary: Beneficiary;
  total_primary_allocation: number;
  total_contingent_allocation: number;
}

export interface BeneficiaryDeleteResponse {
  deleted_beneficiary_id: string;
  total_primary_allocation: number;
  total_contingent_allocation: number;
}

// ─── Request types ────────────────────────────────────────────────────────────

export interface CreateBeneficiaryRequest {
  name: string;
  relationship: BeneficiaryRelationship;
  percentage: number;
  date_of_birth?: string;
  contact_number?: string;
  is_primary: boolean;
  contingent_order?: number;
}

export interface UpdateBeneficiaryRequest
  extends Partial<CreateBeneficiaryRequest> {
  id: string;
}

// ─── Fetch infrastructure ─────────────────────────────────────────────────────

export type BeneficiariesFetchFunction = (
  url: string,
  options?: RequestInit,
) => Promise<Response>;

function defaultFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
}

let _fetch: typeof fetch = fetch;

export function configureBeneficiariesApi(fetchFn: typeof fetch): void {
  _fetch = fetchFn;
}

async function unwrap<T>(response: Response): Promise<T> {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(
      body?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return body.data as T;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const beneficiariesApi = {
  async fetchAll(baseUrl = "/api/v1"): Promise<BeneficiariesListResponse> {
    const res = await _fetch(`${baseUrl}/beneficiaries`);
    return unwrap<BeneficiariesListResponse>(res);
  },

  async fetchById(
    beneficiaryId: string,
    baseUrl = "/api/v1",
  ): Promise<BeneficiaryDetailResponse> {
    const res = await _fetch(`${baseUrl}/beneficiaries/${beneficiaryId}`);
    return unwrap<BeneficiaryDetailResponse>(res);
  },

  async create(
    beneficiary: CreateBeneficiaryRequest,
    baseUrl = "/api/v1",
  ): Promise<BeneficiaryMutationResponse> {
    const res = await _fetch(`${baseUrl}/beneficiaries`, {
      method: "POST",
      body: JSON.stringify(beneficiary),
    });
    return unwrap<BeneficiaryMutationResponse>(res);
  },

  async update(
    beneficiary: UpdateBeneficiaryRequest,
    baseUrl = "/api/v1",
  ): Promise<BeneficiaryMutationResponse> {
    const { id, ...data } = beneficiary;
    const res = await _fetch(`${baseUrl}/beneficiaries/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return unwrap<BeneficiaryMutationResponse>(res);
  },

  async delete(
    beneficiaryId: string,
    baseUrl = "/api/v1",
  ): Promise<BeneficiaryDeleteResponse> {
    const res = await _fetch(`${baseUrl}/beneficiaries/${beneficiaryId}`, {
      method: "DELETE",
    });
    return unwrap<BeneficiaryDeleteResponse>(res);
  },
};

export const RELATIONSHIPS: readonly {
  value: BeneficiaryRelationship;
  label: string;
  color: string;
}[] = [
  { value: "spouse", label: "Spouse", color: "bg-pink-100 text-pink-800" },
  { value: "child", label: "Child", color: "bg-blue-100 text-blue-800" },
  { value: "parent", label: "Parent", color: "bg-purple-100 text-purple-800" },
  { value: "sibling", label: "Sibling", color: "bg-green-100 text-green-800" },
  {
    value: "grandparent",
    label: "Grandparent",
    color: "bg-orange-100 text-orange-800",
  },
  {
    value: "grandchild",
    label: "Grandchild",
    color: "bg-cyan-100 text-cyan-800",
  },
  {
    value: "business_partner",
    label: "Business Partner",
    color: "bg-yellow-100 text-yellow-800",
  },
  { value: "other", label: "Other", color: "bg-gray-100 text-gray-800" },
] as const;
